import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, Save, Sparkles, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { STATUS_META, PIPELINE_COLUMNS, type ContentStatus } from "@/lib/contentOps/statuses";
import type { ArticleRow } from "@/lib/contentOps/types";
import { updateArticle, deleteArticle, runPublishCheck } from "@/lib/contentOps/api";
import BriefTab from "./tabs/BriefTab";
import DraftTab from "./tabs/DraftTab";
import SeoTab from "./tabs/SeoTab";
import PublishTab from "./tabs/PublishTab";
import RevisionsDrawer from "./RevisionsDrawer";

interface Props { articleId: string; onClose: () => void; defaultTab?: string }

const slugify = (s: string) =>
  s.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

const ArticleWorkspace = ({ articleId, onClose, defaultTab = "draft" }: Props) => {
  const [article, setArticle] = useState<ArticleRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const [tab, setTab] = useState(defaultTab);
  const awaitingFixRef = useRef(false);

  useEffect(() => {
    const onJump = (e: Event) => {
      const detail = (e as CustomEvent).detail as { tab?: string; field?: string } | undefined;
      if (!detail?.tab) return;
      setTab(detail.tab);
      if (detail.field) {
        setTimeout(() => {
          const el = document.querySelector(`[data-field="${detail.field}"]`) as HTMLElement | null;
          if (!el) return;
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          const focusTarget =
            (el.matches("input, textarea, select, [tabindex]") ? el : el.querySelector("input, textarea, select, [tabindex]")) as HTMLElement | null;
          focusTarget?.focus?.({ preventScroll: true });
          el.classList.add("contentops-fix-ring");
          window.setTimeout(() => el.classList.remove("contentops-fix-ring"), 2000);
        }, 140);
      }
    };
    const onAwaitingFix = (e: Event) => {
      const detail = (e as CustomEvent).detail as { articleId?: string } | undefined;
      if (detail?.articleId === articleId) awaitingFixRef.current = true;
    };
    const onReload = (e: Event) => {
      const detail = (e as CustomEvent).detail as { id?: string } | undefined;
      if (detail?.id === articleId) load();
    };
    window.addEventListener("contentops:jump", onJump);
    window.addEventListener("contentops:awaiting-fix", onAwaitingFix);
    window.addEventListener("contentops:reload-article", onReload);
    return () => {
      window.removeEventListener("contentops:jump", onJump);
      window.removeEventListener("contentops:awaiting-fix", onAwaitingFix);
      window.removeEventListener("contentops:reload-article", onReload);
    };
  }, [articleId]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("blog_articles").select("*").eq("id", articleId).maybeSingle();
    setLoading(false);
    if (error || !data) {
      toast.error("Failed to load article");
      onClose();
      return;
    }
    setArticle(data as unknown as ArticleRow);
  };

  useEffect(() => { load(); }, [articleId]);

  const patch = async (p: Partial<ArticleRow>) => {
    if (!article) return;
    // Auto-derive slug from title when slug is empty
    const finalPatch: Partial<ArticleRow> = { ...p };
    const nextTitle = p.title ?? article.title;
    const currentSlug = (p.slug ?? article.slug) ?? "";
    if (!currentSlug && nextTitle && p.slug === undefined) {
      finalPatch.slug = slugify(nextTitle);
    }
    setArticle({ ...article, ...finalPatch });
    setSaving(true);
    try {
      await updateArticle(article.id, finalPatch);
    } catch (e) {
      toast.error("Save failed", { description: (e as Error).message });
    } finally {
      setSaving(false);
    }
    // If user was sent away to fix a publish blocker, re-check silently
    // and snap back to Publish tab the moment everything passes.
    if (awaitingFixRef.current && tab !== "publish") {
      try {
        const r = await runPublishCheck(article.id);
        if (r.ready) {
          awaitingFixRef.current = false;
          toast.success("All blocking issues resolved", { description: "Returning to Publish." });
          setTab("publish");
        }
      } catch { /* ignore */ }
    }
  };

  const remove = async () => {
    if (!article) return;
    if (!confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    try {
      await deleteArticle(article.id);
      toast.success("Article deleted");
      onClose();
    } catch (e) {
      toast.error("Delete failed", { description: (e as Error).message });
    }
  };

  if (loading || !article) {
    return (
      <div className="flex items-center justify-center h-64" style={{ color: "hsl(0 0% 50%)" }}>
        <Loader2 size={18} className="animate-spin mr-2" /> Loading workspace…
      </div>
    );
  }

  const meta = STATUS_META[article.status as ContentStatus];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <Button variant="outline" size="sm" onClick={onClose}
            className="border mt-1" style={{ borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 70%)", background: "transparent" }}>
            <ArrowLeft size={14} className="mr-1" /> Pipeline
          </Button>
          <div className="min-w-0">
            <Input
              value={article.title}
              onChange={(e) => setArticle({ ...article, title: e.target.value })}
              onBlur={() => patch({ title: article.title })}
              className="text-lg font-semibold border-0 px-0 h-auto bg-transparent focus-visible:ring-0"
              style={{ color: "hsl(0 0% 95%)" }}
            />
            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs" style={{ color: "hsl(0 0% 50%)" }}>
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
              <span>/{article.slug}</span>
              <span>·</span>
              <span>{article.category}</span>
              {typeof article.seo_score === "number" && <><span>·</span><span>SEO {article.seo_score}</span></>}
              {saving && <><span>·</span><span className="flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Saving</span></>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={article.status} onValueChange={(v) => patch({ status: v as ContentStatus })}>
            <SelectTrigger className="w-44" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* Published status is set only via the Publish button (gated by checks) */}
              {PIPELINE_COLUMNS.concat("refresh_needed").filter((s) => s !== "published").map((s) =>
                <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setShowRevisions(true)}
            className="border" style={{ borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 70%)", background: "transparent" }}>
            <Eye size={14} className="mr-1" /> Revisions
          </Button>
          <Button variant="outline" size="sm" onClick={remove}
            className="border" style={{ borderColor: "hsl(0 75% 40%)", color: "hsl(0 75% 65%)", background: "transparent" }}>
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b w-full justify-start rounded-none h-auto p-0"
          style={{ borderColor: "hsl(0 0% 14%)" }}>
          {["brief", "draft", "seo", "publish"].map((id) => (
            <TabsTrigger key={id} value={id}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[hsl(207_68%_55%)] data-[state=active]:bg-transparent data-[state=active]:text-[hsl(0_0%_95%)] data-[state=active]:shadow-none px-4 py-2.5 text-sm capitalize"
              style={{ color: "hsl(0 0% 55%)" }}>
              {id}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="brief" className="mt-5">
          <BriefTab article={article} onArticleChange={setArticle} />
        </TabsContent>
        <TabsContent value="draft" className="mt-5">
          <DraftTab article={article} onArticleChange={setArticle} onSavePatch={patch} />
        </TabsContent>
        <TabsContent value="seo" className="mt-5">
          <SeoTab article={article} onSavePatch={patch} onReload={load} />
        </TabsContent>
        <TabsContent value="publish" className="mt-5">
          <PublishTab article={article} onSavePatch={patch} onReload={load} />
        </TabsContent>
      </Tabs>

      {showRevisions && (
        <RevisionsDrawer articleId={article.id} onClose={() => setShowRevisions(false)} />
      )}
    </div>
  );
};

export default ArticleWorkspace;
